import { useState } from 'react';
import { db } from '@/lib/firebase';
import { useAuth } from '@/features/auth/AuthProvider';
import { writeAuditLog } from '@/lib/auditLog';

export interface LogActionParams {
    action: string;
    module: string;
    targetId?: string;
    targetName?: string;
    details?: string;
    status?: 'success' | 'failure';
}

/**
 * Thin hook wrapper around the canonical `writeAuditLog` helper
 * (Task #21). Auto-fills the actor from `useAuth().currentUser`,
 * swallows errors so audit failures never break user flow, and
 * tracks a `logging` boolean for UI spinners.
 *
 * Note: per Task #6 client-side `audit_logs` create is rule-denied —
 * `logAction` will resolve quickly (the catch swallows the rejection),
 * but the write does not actually land. Server-side audit entries from
 * Cloud Functions remain the canonical path.
 */
export const useAuditLog = () => {
    const { currentUser } = useAuth();
    const [logging, setLogging] = useState(false);

    const logAction = async (params: LogActionParams) => {
        if (!currentUser) return;

        setLogging(true);
        try {
            await writeAuditLog(db, {
                action: params.action,
                module: params.module,
                actorId: currentUser.uid,
                actorName: currentUser.displayName || currentUser.email || 'Unknown',
                targetId: params.targetId,
                targetName: params.targetName,
                details: params.details,
                status: params.status,
            });
        } catch (error) {
            console.error("Failed to write audit log:", error);
            // Optionally toast on failure, but usually we don't want to block user flow
        } finally {
            setLogging(false);
        }
    };

    return { logAction, logging };
};
